<template>
  <v-card
    flat
    class="ma-3 pa-6"
  >
    <v-row align="center" class="pl-0 mb-4 pt-3 pr-3">
      <v-card-title>
        Purchase Price Report
      </v-card-title>
      <v-spacer></v-spacer>
      <v-btn
        class="ml-4"
        depressed
        color="secondary"
        :loading="isExporting"
        :disabled="!totalCount || !user.can('purchase-price-log.export')"
        @click="downloadExport()"
      >
        <v-icon
          small
          left
        >fas fa-file-export</v-icon>
        Export CSV
      </v-btn>
    </v-row>

    <v-row class="px-3 mb-2" dense>
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="period"
          :items="periodOptions"
          label="Period"
          outlined
          dense
          hide-details
          @change="periodChange()"
        ></v-select>
      </v-col>
      <v-col cols="6" sm="3" md="2">
        <date-picker
          v-model="filter.from"
          label="From"
          outlined
          dense
          hide-details
          :disabled="period !== 'custom'"
          @change="search()"
        ></date-picker>
      </v-col>
      <v-col cols="6" sm="3" md="2">
        <date-picker
          v-model="filter.until"
          label="Until"
          outlined
          dense
          hide-details
          :disabled="period !== 'custom'"
          @change="search()"
        ></date-picker>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete
          v-model="filter.supplier_id"
          :items="suppliers"
          item-value="id"
          item-text="name"
          label="Suppliers"
          multiple
          clearable
          clear-icon="fas fa-times"
          outlined
          dense
          hide-details
          @change="search()"
          @click:clear="search()"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <v-select
          v-model="filter.source"
          :items="sourceOptions"
          label="Source"
          multiple
          clearable
          clear-icon="fas fa-times"
          outlined
          dense
          hide-details
          @change="search()"
          @click:clear="search()"
        ></v-select>
      </v-col>
    </v-row>

    <v-row class="px-3 mb-4" dense align="center">
      <v-col cols="12" sm="6" md="4">
        <v-text-field
          v-model="filter.query"
          label="Search SKU or variant code"
          append-icon="fas fa-search"
          clearable
          clear-icon="fas fa-times"
          outlined
          dense
          hide-details
          single-line
          @input="search()"
          @click:clear="search()"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-checkbox
          v-model="includeNew"
          label="Include new supplier prices (no previous price)"
          dense
          hide-details
          class="mt-0"
          @change="includeNewChange()"
        ></v-checkbox>
      </v-col>
    </v-row>

    <v-simple-table
      v-if="summary.length"
      dense
      class="summary mb-6"
    >
      <thead>
        <tr>
          <th class="text-left">Supplier</th>
          <th class="text-right">Changes</th>
          <th class="text-right">Increased</th>
          <th class="text-right">Decreased</th>
          <th v-if="includeNew" class="text-right">New</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in summary"
          :key="row.supplier_id"
          class="clickable"
          @click="supplierClick(row.supplier_id)"
        >
          <td>{{ row.supplier_name }} <span class="grey--text">{{ row.supplier_code }}</span></td>
          <td class="text-right">{{ row.total }}</td>
          <td class="text-right red--text">{{ row.increased || '' }}</td>
          <td class="text-right green--text">{{ row.decreased || '' }}</td>
          <td v-if="includeNew" class="text-right">{{ row.new || '' }}</td>
        </tr>
        <tr class="font-weight-bold">
          <td>Total</td>
          <td class="text-right">{{ summaryTotals.total }}</td>
          <td class="text-right red--text">{{ summaryTotals.increased || '' }}</td>
          <td class="text-right green--text">{{ summaryTotals.decreased || '' }}</td>
          <td v-if="includeNew" class="text-right">{{ summaryTotals.new || '' }}</td>
        </tr>
      </tbody>
    </v-simple-table>

    <v-data-table
      :headers="headers"
      :items="items"
      :options.sync="options"
      :server-items-length="totalCount"
      :footer-props="{ 'items-per-page-options': [25, 50, 100, 250, 500] }"
      :loading="isSearching"
      class="clickable"
      @click:row="rowClick"
    >

      <template v-slot:item.created_at="{ value }">
        {{ formatDateTime(value) }}
      </template>

      <template v-slot:item.description="{ value }">
        <div class="text-truncate" style="max-width: 320px">{{ value || '—' }}</div>
      </template>

      <template v-slot:item.old_price="{ value }">
        {{ value !== null ? formatCurrency(value) : '—' }}
      </template>

      <template v-slot:item.new_price="{ value }">
        {{ formatCurrency(value) }}
      </template>

      <template v-slot:item.difference="{ item }">
        <span :class="differenceClass(item)">
          {{ item.difference !== null ? (item.difference > 0 ? '+' : '') + formatCurrency(item.difference) : '—' }}
        </span>
      </template>

      <template v-slot:item.difference_percentage="{ item }">
        <span :class="differenceClass(item)">
          {{ item.difference_percentage !== null ? (item.difference_percentage > 0 ? '+' : '') + formatPercentage(item.difference_percentage, 1) : '' }}
        </span>
      </template>

      <template v-slot:item.status="{ value }">
        <v-chip :color="statusColor(value)" x-small dark>{{ value }}</v-chip>
      </template>

    </v-data-table>

  </v-card>

</template>

<script>
import { inject, ref, computed, watch } from 'vue'
import moment from 'moment'
import { useRouter } from 'vue-router/composables'
import { formatCurrency, formatDateTime, formatPercentage } from '@/utils'
import DatePicker from '@/components/DatePicker'

export default {
  components: {
    DatePicker,
  },
  setup() {
    const api = inject('api')
    const router = useRouter()

    // ----- period presets (dates are Y-m-d, 'until' is inclusive in the UI, exclusive towards the API)
    const periodOptions = [
      { text: 'Last 7 days', value: 'last7' },
      { text: 'This week', value: 'this-week' },
      { text: 'Last week', value: 'last-week' },
      { text: 'Last 30 days', value: 'last30' },
      { text: 'This month', value: 'this-month' },
      { text: 'Last month', value: 'last-month' },
      { text: 'Custom', value: 'custom' },
    ]

    function periodRange(period) {
      const today = moment().startOf('day')
      switch (period) {
        case 'this-week': return [moment(today).startOf('isoWeek'), moment(today)]
        case 'last-week': return [moment(today).subtract(1, 'week').startOf('isoWeek'), moment(today).subtract(1, 'week').endOf('isoWeek').startOf('day')]
        case 'last30': return [moment(today).subtract(29, 'days'), moment(today)]
        case 'this-month': return [moment(today).startOf('month'), moment(today)]
        case 'last-month': return [moment(today).subtract(1, 'month').startOf('month'), moment(today).subtract(1, 'month').endOf('month').startOf('day')]
        case 'last7':
        default: return [moment(today).subtract(6, 'days'), moment(today)]
      }
    }

    const toTimestamp = (date) => moment(date, 'YYYY-MM-DD').startOf('day').unix()

    const logCollection = api.collection('purchase-price-logs', {
      params: {
        sort: '-created_at',
      },
      filter: {
        from: {
          operator: (field, value) => ['gte', toTimestamp(value)],
          field: 'created_at',
        },
        until: {
          // inclusive day in the UI -> strictly before the next day
          operator: (field, value) => ['lt', toTimestamp(value) + 24 * 60 * 60],
          field: 'created_at',
        },
        supplier_id: 'in',
        source: 'in',
        query: 'query:sku,variant_code',
      },
      store: true,
    })

    const { items, filter, totalCount, options, isSearching } = logCollection
    const search = logCollection.debounceSearch(300)

    const period = ref(logCollection.params.value.period || 'last7')
    const includeNew = ref(!!logCollection.params.value.include_new)

    function applyPeriod() {
      if (period.value === 'custom') {
        return
      }
      const [from, until] = periodRange(period.value)
      filter.value.from = from.format('YYYY-MM-DD')
      filter.value.until = until.format('YYYY-MM-DD')
    }

    function periodChange() {
      logCollection.setParam('period', period.value)
      applyPeriod()
      search()
    }

    function includeNewChange() {
      logCollection.setParam('include_new', includeNew.value ? 1 : null)
      search()
    }

    if (!filter.value.from && !filter.value.until) {
      applyPeriod()
      logCollection.setParam('period', period.value)
    }

    // ----- summary per supplier, follows the same filter
    const summary = ref([])

    function loadSummary() {
      const params = logCollection._getParams()
      delete params.page
      delete params['per-page']
      delete params.sort
      api.client.get('purchase-price-logs/summary', { params }).then(res => {
        summary.value = res.data || []
      })
    }

    const summaryTotals = computed(() => summary.value.reduce((totals, row) => ({
      total: totals.total + row.total,
      increased: totals.increased + row.increased,
      decreased: totals.decreased + row.decreased,
      new: totals.new + row.new,
    }), { total: 0, increased: 0, decreased: 0, new: 0 }))

    watch(() => isSearching.value, (searching, wasSearching) => {
      if (wasSearching && !searching) {
        loadSummary()
      }
    })

    function supplierClick(supplierId) {
      filter.value.supplier_id = [supplierId]
      search()
    }

    // ----- export
    const isExporting = ref(false)

    function downloadExport() {
      const params = logCollection._getParams()
      delete params.page
      delete params['per-page']
      delete params.sort

      isExporting.value = true
      api.client.post('purchase-price-logs/export', params).then(res => {
        const link = document.createElement('a')
        link.href = res.data.data_url
        link.download = `PurchasePrices_${filter.value.from || ''}_${filter.value.until || ''}.csv`
        link.click()
      }).finally(() => {
        isExporting.value = false
      })
    }

    // ----- filter sources
    const { items: suppliers } = api.collection('suppliers', { params: { sort: 'name' }}).all()

    const sourceOptions = [
      { text: 'Import', value: 'import' },
      { text: 'API', value: 'api' },
      { text: 'Manual', value: 'manual' },
    ]

    const headers = [
      { text: 'Changed at', value: 'created_at', width: '12%' },
      { text: 'Supplier', value: 'supplier_name', sortable: false },
      { text: 'SKU', value: 'sku' },
      { text: 'Variant code', value: 'variant_code' },
      { text: 'Description', value: 'description', sortable: false },
      { text: 'Old price', value: 'old_price', align: 'end' },
      { text: 'New price', value: 'new_price', align: 'end' },
      { text: 'Difference', value: 'difference', align: 'end' },
      { text: '%', value: 'difference_percentage', align: 'end' },
      { text: 'Status', value: 'status', sortable: false },
      { text: 'Source', value: 'source', sortable: false },
    ]

    function rowClick(item) {
      if (item.product_variant_id) {
        router.push({ name: 'variant', params: { id: item.product_variant_id } })
      }
    }

    function differenceClass(item) {
      if (item.difference === null) return ''
      return item.difference > 0 ? 'red--text' : (item.difference < 0 ? 'green--text' : '')
    }

    function statusColor(status) {
      switch (status) {
        case 'increase': return 'red'
        case 'decrease': return 'green'
        case 'new': return 'blue'
        default: return 'grey'
      }
    }

    return {
      user: api.user,
      items,
      filter,
      totalCount,
      options,
      isSearching,
      search,
      period,
      periodOptions,
      periodChange,
      includeNew,
      includeNewChange,
      summary,
      summaryTotals,
      supplierClick,
      isExporting,
      downloadExport,
      suppliers,
      sourceOptions,
      headers,
      rowClick,
      differenceClass,
      statusColor,
      formatCurrency,
      formatDateTime,
      formatPercentage,
    }

  },
}
</script>

<style scoped>

:deep(.v-data-table.clickable table tbody tr:not(.v-data-table__empty-wrapper):hover),
.summary tbody tr.clickable:hover {
  cursor: pointer;
}

</style>
